import React, { useEffect, useState, useMemo } from 'react';
import { useDeepCompareEffect } from 'ahooks';
import './index.scss';
import { Button, Dropdown, SegmentMultiple, Checkbox } from 'tea-component';
import IconDom from '../ItemDom/IconDom';
import { getLocale } from 'umi';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { getPCConsoleMetricValues } from '@intelligence/pages/TopCharts/PCGames/_config/utils';
import MetricBox from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricSelect/MetricBox';
import MetricSearchBox from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricSelect/MetricSearchBox';
import { getMetricOptionByValue } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import InfoDocEN from '@intelligence/_locales/en-US';
import InfoDocCN from '@intelligence/_locales/zh-CN';

const getLegacyMetricType = (metric: any) => metric?.segType;

interface MetricSelectorProp {
  // 弹窗列表，未过滤
  metricTypeOptions: any[];
  sourcesOptions: any[];
  metricsOptions: any[];
  // 上次用户选择的值

  metricTypeVals: string[];
  sourceVals: string[];
  metricsVals: string[];
  // reset默认值

  defaultMetricTypeVals: string[];
  defaultSourceVals: string[];
  defaultMetricsVals: string[];
  // 交互函数
  hendalConfirm: (params: {
    metricsObj: any;
    metricsOptions: any[];
    metricsValue: string[];
    metricTypeValue: string[];
    sourcesValue: string[];
  }) => void;
  loading: boolean;
  dateGranularityType?: 'daily' | 'weekly' | 'monthly';
  resolveMetricType?: (metric: any) => string | undefined;
  disableSourcesWithoutMetrics?: boolean;
}

const normalizeDateGranularityType = (
  dateType = '',
): 'daily' | 'weekly' | 'monthly' => {
  const dateTypeMap: Record<string, 'daily' | 'weekly' | 'monthly'> = {
    day: 'daily',
    date: 'daily',
    daily: 'daily',
    week: 'weekly',
    weekly: 'weekly',
    month: 'monthly',
    monthly: 'monthly',
  };
  return dateTypeMap[dateType] || 'monthly';
};

const MetricSelect = (props: MetricSelectorProp) => {
  const {
    metricTypeVals = [],
    sourceVals = [],
    metricsVals = [],
    metricsOptions = [], // 全部指标
    sourcesOptions = [],
    metricTypeOptions = [],
    defaultMetricTypeVals = [],
    defaultSourceVals = [],
    defaultMetricsVals = [],
    hendalConfirm,
    loading,
    dateGranularityType = 'monthly',
    resolveMetricType = getLegacyMetricType,
    disableSourcesWithoutMetrics = false,
  } = props;

  const { formatMessage } = useIntlExtend();

  const locale = getLocale();
  const normalizedDateGranularityType = normalizeDateGranularityType(dateGranularityType);
  const [inputValue, setInputValue] = useState('');
  const [metricTypeValue, setMetricTypeValue] = useState<string[]>(metricTypeVals);
  const [sourcesValue, setSourcesValue] = useState<string[]>(sourceVals);
  const [metricsValue, setMetricsValue] = useState<string[]>(metricsVals);
  // 取metrictype, datasoure交集后得到的指标
  const [metricOptionsFromBoth, setMetricOptionsFromBoth] = useState<any[]>([]);

  const selectMetricsList = useMemo(() => {
    if (!metricsValue?.length) {
      return [];
    }
    if (metricsValue.includes('all')) {
      return metricOptionsFromBoth;
    }
    return getMetricOptionByValue(metricsValue, false, metricsOptions);
  }, [metricsValue, metricOptionsFromBoth, metricsOptions]);

  const closeDropdown = () => {
    // 模拟窗口点击，关闭下拉弹窗
    const evn = document.createEvent('MouseEvents');
    evn.initEvent('mousedown', true, true);
    document.body.dispatchEvent(evn);
  };
  const renderMetricsOptions = (params: {
    opts: any[];
    showTotal: boolean;
    hasAll: boolean;
    showIcon: boolean;
    showCheckbox?: boolean;
  }) => {
    const { opts, showTotal, hasAll, showIcon, showCheckbox } = params;
    const optionData = opts?.map((e) => {
      const isChecked = metricsValue?.includes(e.value);
      const iconDom = showIcon ? (
        <IconDom
          data={{
            text: formatMessage({ id: e?.text || e?.value }),
            img: getDataSourceIcon(e?.icon ? e?.icon : e?.dataSource || e?.value),
          }}
        />
      ) : (
        formatMessage({ id: e?.text || e?.value })
      );

      return {
        ...e,
        value: e?.value,
        textStr_tmp: formatMessage({ id: e?.text || e?.value }),
        key: e?.text || e?.value,
        text: showCheckbox ? (
          <div className="dbi-search-auto-complete-item">
            <Checkbox value={isChecked} />
            {iconDom}
          </div>
        ) : showIcon ? (
          iconDom
        ) : (
          formatMessage({ id: e?.text || e?.value })
        ),
      };
    });
    if (hasAll) {
      optionData.unshift({
        text: (
          <>
            {showTotal
              ? `${formatMessage({ id: 'dbi_all' })}(${opts?.length})`
              : formatMessage({ id: 'dbi_all' })}
          </>
        ),
        textStr_tmp: 'All',
        value: 'all',
      });
    }
    return optionData;
  };

  const renderMetricsOptionsDom = useMemo(() => {
    const ifHasNewzoo = sourcesValue?.includes('newzoo');
    const ifHasAlinea = sourcesValue?.includes('alinea');
    const getIconCode = (val: string) => {
      if (ifHasNewzoo && val === 'newzoo') return 'newzoowhite';
      if (ifHasAlinea && val === 'alinea') return 'alineawhite';
      return val;
    };
    const optionData = sourcesOptions?.map((e: any) => {
      const hasMetricForSource = metricsOptions.some((metric) => metric?.dataSourceType === e.value);
      // 保留 Source 原有禁用状态；仅在调用方明确开启时，叠加当前粒度无指标的禁用条件。
      const disabled = Boolean(
        e?.disabled || (disableSourcesWithoutMetrics && !hasMetricForSource),
      );
      return {
        ...e,
        value: e?.value,
        disabled,
        textStr_tmp: formatMessage({ id: e?.text }),
        key: e?.text,
        text: (
          <IconDom
            data={{
              text: formatMessage({ id: e?.text || e?.value }),
              img: getDataSourceIcon(getIconCode(e?.value)),
            }}
          />
        ),
      };
    });
    optionData.unshift({
      text: <>{`${formatMessage({ id: 'dbi_all' })}(${sourcesOptions?.length})`}</>,
      textStr_tmp: 'All',
      value: 'all',
    });
    return optionData;
  }, [sourcesOptions, metricsOptions, sourcesValue, disableSourcesWithoutMetrics, formatMessage]);

  const searchData = renderMetricsOptions({
    opts: metricsOptions,
    hasAll: false,
    showTotal: false,
    showIcon: true,
    showCheckbox: true,
  });
  const searchOptions = inputValue
    ? searchData.filter((t) => {
        // 中英文都可搜索
        const name =
          (InfoDocEN as any)?.[t?.key] + (InfoDocCN as any)?.[t?.key] + t?.textStr_tmp;
        return name?.toLocaleLowerCase().includes(inputValue?.toLocaleLowerCase());
      })
    : undefined;
  useEffect(() => {
    setMetricsValue(metricsVals);
  }, [JSON.stringify(metricsVals)]);

  useEffect(() => {
    const _metricTypeValue = metricTypeValue?.includes('all')
      ? metricTypeOptions.map((t) => t.value)
      : metricTypeValue;
    const _sourcesValue = sourcesValue?.includes('all')
      ? sourcesOptions.map((t) => t.value)
      : sourcesValue;
    const metricValueFromBoth = metricsOptions.filter((t) => {
      return _sourcesValue?.includes(t?.dataSourceType) && _metricTypeValue?.includes(resolveMetricType(t));
    });
    setMetricOptionsFromBoth(metricValueFromBoth);
    /** 类型或来源变化后，仅保留仍属于交集的已选指标；all 表示交集全选，不应被拆成明细值。 */
    if (!metricsValue?.includes('all')) {
      const validMetricValues = new Set(metricValueFromBoth.map((metric) => metric?.value));
      const nextMetricsValue = metricsValue.filter((value) => validMetricValues.has(value));
      setMetricsValue(nextMetricsValue);
    }
  }, [
    JSON.stringify(metricTypeValue),
    JSON.stringify(sourcesValue),
    JSON.stringify(metricsValue),
    JSON.stringify(metricsOptions),
    resolveMetricType,
  ]);

  // 接收2个参数，返回一个函数
  const createOnChange = (currentValue: string[], setValue: Function) => (value: string[]) => {
    const hasAll = currentValue.includes('all');
    if (value.length === 0) return;
    // 选择了all，但是非全选，则给它全选
    if (value[0] === 'all') {
      // 如果有All, 那么根据之前是否有all, 判断是删除all, 还是只保留all
      hasAll ? value.shift() : (value = ['all']);
    }
    setValue?.(value);
  };

  const metricRender = loading
    ? []
    : renderMetricsOptions({
        opts: metricsValue?.includes('all') ? metricOptionsFromBoth : selectMetricsList,
        hasAll: false,
        showTotal: false,
        showIcon: true,
      });

  // 这里需要根据父组件传入的metricTypeVals、sourceVals、metricsVals，更新组件内部的state
  useDeepCompareEffect(() => {
    setMetricTypeValue(metricTypeVals || []);
    setSourcesValue(sourceVals || []);
    setMetricsValue(metricsVals || []);
  }, [metricTypeVals, sourceVals, metricsVals]);

  const metricsValueForBox = useMemo(() => {
    if (metricsValue?.includes('all')) {
      return metricOptionsFromBoth.map((item) => item.value);
    }
    return metricsValue;
  }, [metricsValue, metricOptionsFromBoth]);

  const handleMetricBoxChange = (value: string[]) => {
    if (!value?.length) {
      setMetricsValue([]);
      return;
    }
    if (
      metricOptionsFromBoth.length > 0 &&
      value.length === metricOptionsFromBoth.length
    ) {
      setMetricsValue(['all']);
      return;
    }
    setMetricsValue(value);
  };

  const handleSearchMetricChange = (
    value: string[] | ((prev: string[]) => string[]),
  ) => {
    setMetricsValue((prev) => {
      const nextValues = typeof value === 'function' ? value(prev || []) : value;
      if (!Array.isArray(nextValues) || nextValues.includes('all')) {
        return nextValues;
      }

      const addedMetrics = nextValues.filter((metric) => !prev?.includes(metric));
      addedMetrics.forEach((metric) => {
        const item = searchData?.find((opt) => opt?.value === metric);
        if (!item) return;
        // 点击选中的指标，要同时选中它的类型和数据源，并把交集指标展示出来，但未选中
        if (!metricTypeValue.includes('all') && item?.segType) {
          setMetricTypeValue((current) =>
            current.includes(item.segType) ? current : [...current, item.segType],
          );
        }
        if (!sourcesValue.includes('all') && item?.dataSourceType) {
          setSourcesValue((current) =>
            current.includes(item.dataSourceType)
              ? current
              : [...current, item.dataSourceType],
          );
        }
      });

      return nextValues;
    });
  };

  return (
    <>
      <Dropdown
        // open={openDropdown}
        matchButtonWidth
        className={`dbi-metric-select-box ${locale == 'en-US' ? 'en' : 'ch'}`}
        overlayClassName="dbi-metric-select-overlay"
        button={
          <div className="dbi-metric-select">
            <div className="dbi-metric-select-lable">
              {formatMessage({ id: 'dbi_top_metric_intotal' }, { number: metricRender?.length })}
            </div>
            <div className="dbi-metric-select-text">
              <div className="dbi-metric-select-value">
                {metricRender?.map((e: any, index: number) => {
                  return (
                    <React.Fragment key={`${e.text}-${e?.value}-${index}`}>
                      <span className="dbi-metric-select-span">
                        {e.text}
                        {index !== metricRender?.length - 1 && <>, </>}
                      </span>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        }
        appearance="button"
        clickClose={false}
        footer={
          <div className="dbi-metric-footer">
            <Button
              onClick={() => {
                closeDropdown();
                // 对数据进得处理
                const parseValue = metricRender?.map((t) => {
                  return {
                    ...t,
                    value: t?.value,
                    text: t?.key ?? t?.textStr_tmp, // t?.textStr_tmp ?? t?.text,
                    textStr_tmp: undefined,
                  };
                });
                const metricsObj = getPCConsoleMetricValues(parseValue);
                hendalConfirm?.({
                  metricsObj,
                  metricsOptions: parseValue,
                  metricsValue,
                  metricTypeValue,
                  sourcesValue,
                });
              }}
              type="primary"
              disabled={
                metricsValue?.length === 0 ||
                (metricOptionsFromBoth?.length === 0 && metricsValue?.includes('all'))
              }
            >
              {formatMessage({ id: 'dbi_confirm' })}
            </Button>
            <Button
              onClick={() => {
                // closeDropdown();
                // Reset展示为产品给的默认指标
                setMetricsValue(defaultMetricsVals);
                setMetricTypeValue(defaultMetricTypeVals);
                setSourcesValue(defaultSourceVals);
              }}
            >
              {formatMessage({ id: 'dbi_reset' })}
            </Button>
          </div>
        }
      >
        {(close) => (
          <div className="dbi-metric-box">
            <MetricSearchBox
              inputValue={inputValue}
              setInputValue={setInputValue}
              onChange={handleSearchMetricChange}
              setSourcesValue={setSourcesValue}
              dateGranularityType={normalizedDateGranularityType}
              searchOptions={searchOptions}
              selectMetricsList={selectMetricsList}
            />
            <div className="dbi-metric-form">
              <div className="dbi-metric-row">
                <div className="dbi-metric-lable">
                  {formatMessage({ id: 'dbi_top_metrictype' })}
                </div>
                <div className="dbi-metric-context">
                  <SegmentMultiple
                    value={metricTypeValue}
                    onChange={createOnChange(metricTypeValue, setMetricTypeValue)}
                    options={renderMetricsOptions({
                      opts: metricTypeOptions,
                      hasAll: true,
                      showTotal: false,
                      showIcon: false,
                    })}
                  />
                </div>
              </div>
              <div className="dbi-metric-row">
                <div className="dbi-metric-lable">{formatMessage({ id: 'dbi_top_sources' })}</div>
                <div className="dbi-metric-context">
                  <SegmentMultiple
                    value={sourcesValue}
                    onChange={createOnChange(sourcesValue, setSourcesValue)}
                    options={renderMetricsOptionsDom}
                  />
                </div>
              </div>
              <div className="dbi-metric-row">
                <div className="dbi-metric-lable other-top">
                  {formatMessage({ id: 'dbi_label_metrics' })}
                </div>
                <div className="dbi-metric-context">
                  <div className="metric-box dbi-metric-box-stats">
                    <MetricBox
                      onChange={handleMetricBoxChange}
                      metricsValue={metricsValueForBox}
                      options={metricOptionsFromBoth}
                      dateGranularityType={dateGranularityType}
                    />
                  </div>
                </div>
              </div>
              {/* <div className="dbi-metric-row">
                <Checkbox.Group
                  value={taste}
                  onChange={(value) => setTaste(value)}
                >
                  <Checkbox name="la">Default display</Checkbox>
                </Checkbox.Group>
              </div> */}
            </div>
          </div>
        )}
      </Dropdown>
    </>
  );
};

export default MetricSelect;
